class OwProductCardSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = (event) => {
    const swatch = event.target.closest('[data-ow-product-card-swatch]');
    if (!(swatch instanceof HTMLAnchorElement) || !this.contains(swatch)) return;

    if (
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
    ) {
      return;
    }

    const productCard = this.closest('product-card');
    const state = this.querySelector(`template[data-ow-card-state="${CSS.escape(swatch.dataset.stateId || '')}"]`);
    if (!productCard || !(state instanceof HTMLTemplateElement)) return;

    event.preventDefault();
    this.selectSwatch(productCard, swatch, state);
  };

  selectSwatch(productCard, swatch, state) {
    const productUrl = swatch.dataset.productUrl;
    if (!productUrl) return;

    productCard.classList.add('is-ow-family-switching');

    if (typeof productCard.applyVariantToLinks === 'function') {
      productCard.applyVariantToLinks(null, productUrl);
    } else {
      productCard
        .querySelectorAll(':scope > a.product-card__link, [ref="cardGalleryLink"], [ref="productTitleLink"]')
        .forEach((link) => link.setAttribute('href', productUrl));
    }

    productCard.dataset.productId = swatch.dataset.productId || '';
    if (swatch.dataset.featuredMediaUrl) {
      productCard.dataset.featuredMediaUrl = swatch.dataset.featuredMediaUrl;
    } else {
      productCard.removeAttribute('data-featured-media-url');
    }
    if (swatch.dataset.viewEventPayload) {
      productCard.setAttribute('view-event-payload', swatch.dataset.viewEventPayload);
    }

    const productTitle = swatch.dataset.productTitle || '';
    const gallery = productCard.querySelector('[ref="cardGallery"]');
    if (gallery) {
      gallery.dataset.productId = swatch.dataset.productId || '';
      gallery.querySelectorAll('[ref="cardGalleryLink"]').forEach((link) => {
        link.setAttribute('aria-label', productTitle);
      });
      this.updateImages(gallery, swatch, productTitle);
      this.updateBadge(gallery, state);
    }

    const overlayLink = productCard.querySelector(':scope > .product-card__link');
    overlayLink?.setAttribute('aria-label', productTitle);

    if (this.dataset.groupedTitle !== 'true') {
      const title = productCard.querySelector('[ref="productTitleLink"] [role="heading"]');
      if (title) title.textContent = productTitle;
    }
    const zoomTitle = productCard.querySelector('.product-grid-view-zoom-out--details h3');
    if (zoomTitle) zoomTitle.textContent = productTitle;

    this.updatePrice(productCard, state);
    this.updateSelection(swatch);
    this.refreshQuickAdd(productCard);

    requestAnimationFrame(() => productCard.classList.remove('is-ow-family-switching'));
  }

  updateImages(gallery, swatch, productTitle) {
    const featuredSrc = swatch.dataset.featuredSrc;
    if (!featuredSrc) return;

    const featuredSrcset = swatch.dataset.featuredSrcset || featuredSrc;
    const hoverSrc = swatch.dataset.hoverSrc || featuredSrc;
    const hoverSrcset = swatch.dataset.hoverSrcset || featuredSrcset;
    const images = [...gallery.querySelectorAll('img')];

    images.forEach((image, index) => {
      const src = index === 0 ? featuredSrc : hoverSrc;
      const srcset = index === 0 ? featuredSrcset : hoverSrcset;
      image.src = src;
      image.srcset = srcset;
      image.alt = productTitle;
    });

    gallery.querySelectorAll('picture source').forEach((source, index) => {
      source.srcset = index === 0 ? featuredSrcset : hoverSrcset;
    });
  }

  updatePrice(productCard, state) {
    const currentPrice = productCard.querySelector('product-price [ref="priceContainer"]');
    const nextPrice = state.content.querySelector('[data-ow-price-fragment] [ref="priceContainer"]');
    if (currentPrice && nextPrice) currentPrice.replaceWith(nextPrice.cloneNode(true));
  }

  updateBadge(gallery, state) {
    gallery.querySelector(':scope > .product-badges')?.remove();
    gallery.classList.remove(
      'card-gallery--badge-top-left',
      'card-gallery--badge-top-right',
      'card-gallery--badge-bottom-left'
    );
    const nextBadge = state.content.querySelector('[data-ow-badge-fragment] .product-badges');
    if (nextBadge) {
      const positionClass = [...nextBadge.classList].find((className) => className.startsWith('product-badges--'));
      if (positionClass) gallery.classList.add(positionClass.replace('product-badges--', 'card-gallery--badge-'));
      gallery.append(nextBadge.cloneNode(true));
    }
  }

  updateSelection(selectedSwatch) {
    this.querySelectorAll('[data-ow-product-card-swatch]').forEach((swatch) => {
      const current = swatch === selectedSwatch;
      swatch.classList.toggle('is-current', current);
      if (current) swatch.setAttribute('aria-current', 'true');
      else swatch.removeAttribute('aria-current');
    });

    const status = this.querySelector('[data-ow-card-swatch-status]');
    if (status) status.textContent = `${selectedSwatch.getAttribute('aria-label') || 'Product'} selected`;
  }

  refreshQuickAdd(productCard) {
    const quickAdd = productCard.refs?.quickAdd;
    if (quickAdd && typeof quickAdd.fetchProductPage === 'function') {
      quickAdd.fetchProductPage(productCard.productPageUrl);
    }
  }
}

if (!customElements.get('ow-product-card-swatches')) {
  customElements.define('ow-product-card-swatches', OwProductCardSwatches);
}

const deduplicateGroupedResults = (resultsList) => {
  const seen = new Set();
  resultsList.querySelectorAll('[data-ow-group-key]').forEach((card) => {
    const key = card.dataset.owGroupKey;
    if (!key) return;
    if (seen.has(key)) card.remove();
    else seen.add(key);
  });
};

const initializeGroupedResults = (root = document) => {
  root.querySelectorAll('[data-ow-grouped-results]').forEach((resultsList) => {
    if (resultsList.dataset.owGroupingInitialized === 'true') return;
    resultsList.dataset.owGroupingInitialized = 'true';
    deduplicateGroupedResults(resultsList);
    new MutationObserver(() => deduplicateGroupedResults(resultsList)).observe(resultsList, {
      childList: true,
      subtree: true,
    });
  });
};

initializeGroupedResults();
document.addEventListener('shopify:section:load', (event) => initializeGroupedResults(event.target));
